/**
 * Motor de sincronizacion del dispositivo.
 *
 * Es UN solo motor, no llamadas sueltas desde las pantallas. Las pantallas encolan
 * operaciones y leen estado; el motor decide cuando enviar, cuantas veces reintentar
 * y como clasificar cada fallo — usando la maquina de estados de `@senm/domain`, que
 * esta probada de forma aislada.
 */

import type {
  SyncOperation,
  SyncPullResponse,
  SyncPushResponse,
} from "@senm/contracts";
import {
  classifyFailure,
  markFailed,
  markProcessing,
  markSynced,
  recoverInterrupted,
  selectBatch,
  summarize,
  type OutboxEntry,
  type SyncSummary,
} from "@senm/domain";
import { ApiRequestError, apiRequest } from "../api/client.js";
import { db, type LocalOutboxEntry } from "../local/db.js";
import {
  getInstallationId,
  getSession,
  saveSession,
} from "../local/session.js";

const BATCH_SIZE = 25;

function toDomain(entry: LocalOutboxEntry): OutboxEntry {
  return { ...entry } as OutboxEntry;
}

function toLocal(entry: OutboxEntry): LocalOutboxEntry {
  return { ...entry } as LocalOutboxEntry;
}

export async function enqueue(entry: LocalOutboxEntry): Promise<void> {
  await db.outbox.put(entry);
}

export async function outboxSummary(): Promise<SyncSummary> {
  const entries = await db.outbox.toArray();
  return summarize(entries.map(toDomain));
}

export async function listOutbox(): Promise<LocalOutboxEntry[]> {
  return db.outbox.orderBy("createdAt").reverse().toArray();
}

/**
 * Recuperacion tras un cierre abrupto: toda operacion que quedo en PROCESANDO vuelve
 * a PENDIENTE. Es seguro porque la clave idempotente impide duplicar el pago si el
 * servidor si llegó a recibirlo.
 */
export async function recoverAfterRestart(now: string): Promise<number> {
  const entries = await db.outbox
    .where("status")
    .equals("PROCESANDO")
    .toArray();
  if (entries.length === 0) return 0;
  const recovered = recoverInterrupted(entries.map(toDomain), now);
  await db.outbox.bulkPut(recovered.map(toLocal));
  return recovered.length;
}

export interface SyncResult {
  pushed: number;
  accepted: number;
  duplicated: number;
  conflicted: number;
  rejected: number;
  pulled: number;
  /** `true` si el servidor revoco el acceso: hay que detener todo y avisar. */
  revoked: boolean;
  revokedReason: string | null;
  offline: boolean;
}

/**
 * Ciclo completo: revalidar -> enviar la cola -> descargar cambios.
 *
 * La revalidacion va PRIMERO: si el usuario, el rol o el dispositivo ya no estan
 * autorizados, no se envía ni una operación.
 */
export async function synchronize(now: string): Promise<SyncResult> {
  const result: SyncResult = {
    pushed: 0,
    accepted: 0,
    duplicated: 0,
    conflicted: 0,
    rejected: 0,
    pulled: 0,
    revoked: false,
    revokedReason: null,
    offline: false,
  };

  const session = await getSession();
  if (session === null) return result;

  const installationId = await getInstallationId();

  try {
    const revalidation = await apiRequest<{
      valid: boolean;
      offlineValidUntil: string | null;
      revokedReason: string | null;
    }>("/v1/auth/revalidate", { method: "POST", installationId });

    if (!revalidation.valid) {
      result.revoked = true;
      result.revokedReason = revalidation.revokedReason;
      return result;
    }
    if (revalidation.offlineValidUntil !== null) {
      // Cada contacto con el servidor extiende la ventana de operacion offline.
      await saveSession({
        ...session,
        offlineValidUntil: revalidation.offlineValidUntil,
        lastRevalidatedAt: now,
      });
    }
  } catch (error) {
    if (error instanceof ApiRequestError && error.isNetworkFailure) {
      result.offline = true;
      return result;
    }
    throw error;
  }

  await recoverAfterRestart(now);
  Object.assign(result, await pushOutbox(now, installationId));
  result.pulled = await pullChanges(now);
  return result;
}

async function pushOutbox(
  now: string,
  installationId: string,
): Promise<Partial<SyncResult>> {
  const all = (await db.outbox.toArray()).map(toDomain);
  const batch = selectBatch(all, now, BATCH_SIZE);
  if (batch.length === 0) return {};

  // Se marcan como PROCESANDO ANTES de enviar: si el proceso muere a mitad, al
  // reabrir se sabe cuales estaban en vuelo y se recuperan.
  const processing = batch.map((entry) => markProcessing(entry, now));
  await db.outbox.bulkPut(processing.map(toLocal));

  const operations: SyncOperation[] = processing.map(
    (entry) =>
      ({
        type: entry.type,
        operationId: entry.id,
        idempotencyKey: entry.idempotencyKey,
        createdAt: entry.createdAt,
        payload: entry.payload,
      }) as SyncOperation,
  );

  let response: SyncPushResponse;
  try {
    response = await apiRequest<SyncPushResponse>("/v1/sync/push", {
      method: "POST",
      body: { installationId, operations },
      installationId,
    });
  } catch (error) {
    // El lote entero vuelve a la cola con backoff. Nada se marca como sincronizado
    // sin una confirmacion valida del servidor.
    const kind = classifyFailure({
      httpStatus: error instanceof ApiRequestError ? error.httpStatus : null,
      errorCode: error instanceof ApiRequestError ? error.code : null,
    });
    const failed = processing.map((entry) =>
      markFailed(entry, {
        now,
        kind,
        errorCode: error instanceof ApiRequestError ? error.code : null,
        errorMessage: error instanceof Error ? error.message : null,
      }),
    );
    await db.outbox.bulkPut(failed.map(toLocal));
    return { pushed: processing.length };
  }

  const counts = { accepted: 0, duplicated: 0, conflicted: 0, rejected: 0 };
  const updates: LocalOutboxEntry[] = [];

  for (const entry of processing) {
    const outcome = response.results.find(
      (item) => item.operationId === entry.id,
    );
    if (outcome === undefined) {
      // El servidor no reporto esta operacion: se reintenta, nunca se da por buena.
      updates.push(
        toLocal(
          markFailed(entry, {
            now,
            kind: "TRANSITORIO",
            errorCode: null,
            errorMessage: "El servidor no confirmó esta operación",
          }),
        ),
      );
      continue;
    }

    switch (outcome.outcome) {
      case "ACEPTADO":
      case "DUPLICADO":
        counts[outcome.outcome === "ACEPTADO" ? "accepted" : "duplicated"] += 1;
        updates.push(toLocal(markSynced(entry, now)));
        if (outcome.payment !== null)
          await applyConfirmedPayment(outcome.payment, null);
        break;
      case "CONFLICTO":
        counts.conflicted += 1;
        updates.push(
          toLocal(
            markFailed(entry, {
              now,
              kind: "CONFLICTO",
              errorCode: outcome.errorCode,
              errorMessage: outcome.errorMessage,
            }),
          ),
        );
        if (outcome.payment !== null)
          await applyConfirmedPayment(outcome.payment, outcome.errorMessage);
        break;
      default:
        counts.rejected += 1;
        updates.push(
          toLocal(
            markFailed(entry, {
              now,
              kind: "PERMANENTE",
              errorCode: outcome.errorCode,
              errorMessage: outcome.errorMessage,
            }),
          ),
        );
        if (outcome.payment !== null)
          await applyConfirmedPayment(outcome.payment, outcome.errorMessage);
        break;
    }
  }

  await db.outbox.bulkPut(updates);
  return { pushed: processing.length, ...counts };
}

/** Refleja localmente el estado que el servidor confirmo para un pago. */
async function applyConfirmedPayment(
  payment: Record<string, unknown>,
  statusMessage: string | null,
): Promise<void> {
  const existing = await db.payments.get(payment.id as string);
  if (existing === undefined) return;
  await db.payments.update(payment.id as string, {
    status: payment.status as string,
    receiptNumber: (payment.receiptNumber as string | null) ?? null,
    statusMessage,
  });
}

/**
 * Descarga de cambios, pagina a pagina HASTA AGOTARLAS.
 *
 * Antes se pedia una sola pagina y se guardaba el cursor que devolvia el servidor
 * —"ahora"—, aunque el lote viniera cortado en `limit` filas. Todo lo que no cupo
 * quedaba por debajo del cursor y no se volvia a pedir jamas: el dispositivo se
 * quedaba con una parte de los cargos y la caja abria un alumno sin sus
 * mensualidades. Con la base de demostracion nunca se noto porque cabia entera.
 *
 * El cursor se guarda al final y es el de la PRIMERA pagina: lo que cambie
 * mientras dura el recorrido entra en la sincronizacion siguiente en vez de
 * perderse entre dos paginas.
 */
/**
 * Filas por pagina y por entidad. Se envia explicita en vez de confiar en el
 * valor por omision del servidor: el avance del `offset` tiene que ser
 * exactamente el tamano que uso quien respondio.
 */
const PAGINA = 500;

const PAGINAS_MAXIMAS = 200;

async function pullChanges(now: string): Promise<number> {
  const cursorRow = await db.cursors.get("global");
  const since = cursorRow?.cursor ?? null;
  const base = `/v1/sync/pull?limit=${PAGINA}${since === null ? "" : `&since=${encodeURIComponent(since)}`}`;

  let applied = 0;
  let offset = 0;
  let cursorFinal: string | null = null;

  for (let pagina = 0; pagina < PAGINAS_MAXIMAS; pagina += 1) {
    const response = await apiRequest<SyncPullResponse>(
      `${base}&offset=${offset}`,
    );
    cursorFinal ??= response.cursor;
    applied += await aplicarPagina(response, cursorFinal, now);
    if (!response.hasMore) return applied;
    offset += PAGINA;
  }

  // Tope de seguridad: mejor cortar y reintentar en la proxima sincronizacion que
  // girar sin fin si el servidor siempre respondiera `hasMore`.
  return applied;
}

/** Vuelca una pagina en la base local. Devuelve cuantas filas se aplicaron. */
async function aplicarPagina(
  response: SyncPullResponse,
  cursor: string,
  now: string,
): Promise<number> {
  let applied = 0;

  // Aplicacion TRANSACCIONAL: o entra todo el lote o no entra nada. Nunca queda el
  // dispositivo con estudiantes nuevos y cargos viejos.
  await db.transaction(
    "rw",
    db.students,
    db.charges,
    db.concepts,
    db.cursors,
    async () => {
      for (const raw of response.students as unknown as Record<
        string,
        string | number | null
      >[]) {
        await db.students.put({
          id: String(raw.id),
          code: String(raw.code),
          fullName: String(raw.full_name ?? raw.fullName ?? ""),
          searchName: String(raw.search_name ?? raw.searchName ?? ""),
          active: Number(raw.active) === 1,
          cycleId: String(raw.cycle_id ?? ""),
          gradeId: String(raw.grade_id ?? ""),
          gradeName: String(raw.grade_name ?? "Sin grado"),
          sectionId: String(raw.section_id ?? ""),
          sectionName: String(raw.section_name ?? "Sin sección"),
          outstandingMinor: Number(raw.outstanding_minor ?? 0),
          creditMinor: Number(raw.credit_minor ?? 0),
          currency: String(raw.currency ?? "GTQ"),
          version: Number(raw.version ?? 1),
          updatedAt: String(raw.updated_at ?? now),
        });
        applied += 1;
      }

      for (const raw of response.charges as unknown as Record<
        string,
        string | number | null
      >[]) {
        await db.charges.put({
          id: String(raw.id),
          studentId: String(raw.student_id),
          cycleId: String(raw.cycle_id),
          conceptId: String(raw.concept_id),
          conceptName: String(raw.concept_name ?? ""),
          conceptActive: Number(raw.concept_active ?? 1) === 1,
          amountMinor: Number(raw.amount_minor ?? 0),
          appliedMinor: Number(raw.applied_minor ?? 0),
          currency: String(raw.currency ?? "GTQ"),
          dueDate:
            raw.due_date === null || raw.due_date === undefined
              ? null
              : String(raw.due_date),
          voided: Number(raw.voided ?? 0) === 1,
        });
        applied += 1;
      }

      for (const raw of response.concepts as unknown as Record<
        string,
        string | number | null
      >[]) {
        await db.concepts.put({
          id: String(raw.id),
          code: String(raw.code),
          name: String(raw.name),
          active: Number(raw.active ?? 1) === 1,
        });
      }

      // Tombstones: se elimina localmente lo que el servidor marco como borrado.
      for (const [entity, ids] of Object.entries(response.deletedIds ?? {})) {
        if (entity === "estudiantes") await db.students.bulkDelete(ids);
        if (entity === "cargos") await db.charges.bulkDelete(ids);
        if (entity === "conceptos") await db.concepts.bulkDelete(ids);
      }

      await db.cursors.put({ entity: "global", cursor, lastSyncedAt: now });
    },
  );

  return applied;
}

/** Reintento manual de una operacion rechazada, tras corregir lo que fallo. */
export async function retryEntry(id: string, now: string): Promise<void> {
  const entry = await db.outbox.get(id);
  if (entry === undefined) return;
  const { retryAfterCorrection } = await import("@senm/domain");
  await db.outbox.put(toLocal(retryAfterCorrection(toDomain(entry), now)));
}

/** Antigüedad de los datos locales, para avisar que podrian estar desactualizados. */
export async function lastSyncedAt(): Promise<string | null> {
  return (await db.cursors.get("global"))?.lastSyncedAt ?? null;
}

/**
 * Retención de la cola de salida: cuántos días se conservan las operaciones ya
 * sincronizadas antes de purgarlas.
 *
 * No se borran al confirmarse porque el Centro de sincronización debe poder
 * mostrar «qué pasó ayer» cuando alguien pregunta por un cobro. Pero tampoco
 * pueden quedarse para siempre: una caja que registra 40 pagos al día acumularía
 * más de 14 000 entradas al año en IndexedDB, y cada arranque las lee todas.
 */
export const OUTBOX_RETENTION_DAYS = 30;

/**
 * Elimina de la cola las operaciones SINCRONIZADAS más antiguas que la retención.
 *
 * Solo toca las sincronizadas: una entrada rechazada o en conflicto sigue
 * requiriendo una decisión y borrarla haría desaparecer el problema de la vista
 * sin resolverlo. Los pagos en sí NUNCA se purgan — viven en `db.payments` y
 * están confirmados en el servidor; lo que se limpia es el registro de envío.
 */
export async function purgeSyncedOutbox(now: string): Promise<number> {
  const limite = new Date(
    new Date(now).getTime() - OUTBOX_RETENTION_DAYS * 86_400_000,
  ).toISOString();

  const purgables = await db.outbox
    .where("status")
    .equals("SINCRONIZADO")
    .filter((entrada) => entrada.updatedAt < limite)
    .primaryKeys();

  if (purgables.length === 0) return 0;
  await db.outbox.bulkDelete(purgables);
  return purgables.length;
}
